import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { ProductCard } from "../components/ProductCard";
import { ColourSwatch } from "../components/ColourSwatch";
import { Input } from "../components/ui/input";
import { Search, SlidersHorizontal, X, Loader2, Download, MessageCircle } from "lucide-react";
import api from "../lib/api";
import { CATALOGUE_URL, whatsappCatalogueLink } from "../data/constants";

export default function CataloguePage() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colours, setColours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);

  const category = params.get("category") || "";
  const colour = params.get("colour") || "";
  const size = params.get("size") || "";
  const search = params.get("search") || "";

  const palette = useMemo(() => {
    const p = {}; colours.forEach((c) => (p[c.name] = c.hex)); return p;
  }, [colours]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/colours").then((r) => setColours(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category) q.category = category;
    if (colour) q.colour = colour;
    if (size) q.size = size;
    if (search) q.search = search;
    api.get("/products", { params: q })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, colour, size, search]);

  const allSizes = useMemo(() => {
    const s = new Set();
    products.forEach((p) => (p.sizes || []).forEach((x) => s.add(x)));
    return [...s].sort();
  }, [products]);

  const hasFilters = category || colour || size || search;

  const FilterPanel = () => (
    <div className="space-y-7">
      <div>
        <h4 className="mb-3 font-display text-sm font-bold uppercase tracking-wider text-slate-900">Category</h4>
        <div className="flex flex-col gap-1" data-testid="filter-category-select">
          <button onClick={() => setParam("category", "")}
            className={`rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${!category ? "bg-[#ECFDF5] text-[#059669]" : "text-slate-600 hover:bg-slate-100"}`}>
            All Categories
          </button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => setParam("category", c.name)}
              className={`rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${category === c.name ? "bg-[#ECFDF5] text-[#059669]" : "text-slate-600 hover:bg-slate-100"}`}>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h4 className="mb-3 font-display text-sm font-bold uppercase tracking-wider text-slate-900">Colour</h4>
        <div className="flex flex-wrap gap-2.5" data-testid="filter-colour-swatches">
          {colours.map((c) => (
            <button key={c.id} onClick={() => setParam("colour", colour === c.name ? "" : c.name)}
              title={c.name}
              className={`rounded-full p-0.5 transition-all ${colour === c.name ? "ring-2 ring-[#10B981] ring-offset-1" : ""}`}>
              <ColourSwatch name={c.name} palette={palette} size="lg" showTooltip={false} />
            </button>
          ))}
        </div>
      </div>

      {allSizes.length > 0 && (
        <div>
          <h4 className="mb-3 font-display text-sm font-bold uppercase tracking-wider text-slate-900">Size</h4>
          <div className="flex flex-col gap-1" data-testid="filter-size-select">
            <button onClick={() => setParam("size", "")}
              className={`rounded-lg px-3 py-2 text-left font-spec text-xs transition-colors ${!size ? "bg-[#ECFDF5] text-[#059669]" : "text-slate-600 hover:bg-slate-100"}`}>
              All sizes
            </button>
            {allSizes.map((s) => (
              <button key={s} onClick={() => setParam("size", s)}
                className={`rounded-lg px-3 py-2 text-left font-spec text-xs transition-colors ${size === s ? "bg-[#ECFDF5] text-[#059669]" : "text-slate-600 hover:bg-slate-100"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {hasFilters && (
        <button onClick={() => setParams({})}
          className="flex items-center gap-1 text-sm font-semibold text-red-500 hover:underline">
          <X className="h-4 w-4" /> Clear all filters
        </button>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="catalogue-page-container">
        <div className="mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0B1B3D] sm:text-4xl">
                {category || "All Products"}
              </h1>
              <p className="mt-2 text-slate-600">Wholesale flooring & matting — request a quote on anything you see.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={CATALOGUE_URL} target="_blank" rel="noopener noreferrer" data-testid="catalogue-download-btn"
                className="flex items-center gap-2 rounded-full bg-[#0B1B3D] px-5 py-3 text-sm font-bold text-white shadow-md transition-transform hover:scale-105">
                <Download className="h-4 w-4" /> Download Catalogue
              </a>
              <a href={whatsappCatalogueLink()} target="_blank" rel="noopener noreferrer" data-testid="catalogue-whatsapp-share-btn"
                className="flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-bold text-white shadow-md transition-transform hover:scale-105">
                <MessageCircle className="h-4 w-4" /> Share on WhatsApp
              </a>
            </div>
          </div>
        </div>

        <div className="mb-6 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setParam("search", e.target.value)}
              placeholder="Search by name, colour, size..."
              data-testid="catalogue-search-input"
              className="h-12 rounded-xl pl-10" />
          </div>
          <button onClick={() => setMobileFilters(true)}
            className="flex h-12 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 lg:hidden">
            <SlidersHorizontal className="h-4 w-4" /> Filters
          </button>
        </div>

        <div className="flex gap-8">
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-5">
              <FilterPanel />
            </div>
          </aside>

          <div className="flex-1">
            {loading ? (
              <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#10B981]" /></div>
            ) : products.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center">
                <p className="font-display text-lg font-semibold text-slate-700">No products found</p>
                <p className="mt-1 text-sm text-slate-500">Try adjusting your filters or search.</p>
              </div>
            ) : (
              <>
                <p className="mb-4 text-sm text-slate-500">{products.length} product{products.length !== 1 ? "s" : ""}</p>
                <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {products.map((p) => <ProductCard key={p.id} product={p} palette={palette} />)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {mobileFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85%] overflow-y-auto bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold">Filters</h3>
              <button onClick={() => setMobileFilters(false)}><X className="h-6 w-6" /></button>
            </div>
            <FilterPanel />
          </div>
        </div>
      )}
    </Layout>
  );
}
